import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { Observable } from 'rxjs';
import { CommonService } from 'src/@services/common.service';
import { AuthServiceService } from 'src/app/auth-service.service';
import { InnerPagesService } from 'src/app/pages/common-layout/inner-pages.service';
import { environment } from 'src/environments/environment';
import { CommonPagesService } from './common-pages.service';

interface RoutePermission {
  permission: boolean;
  url: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthGuard {
  constructor(
    private _cS: CommonService,
    private _router: Router,
    private _cpS: CommonPagesService,
    private authService: AuthServiceService,
    private _iS: InnerPagesService
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ):
    | Observable<boolean | UrlTree>
    | Promise<boolean | UrlTree>
    | boolean
    | UrlTree {
    var token = this._cS.getTokenObj();
    document.title =
      (route.data['title'] ? route.data['title'] : 'Login') +
      ' - Brooklyn' +
      (environment.apiURL.includes('staging') ? ' Staging' : '');

    const isLoginRoute = state.url.includes('/login');

    if (isLoginRoute) {
      return true;
    }

    const hasTokenParam = route.queryParams['t'];
    const hasEmailParam = route.queryParams['e'];
    if (hasTokenParam && hasEmailParam) {
      return true;
    }

    if (!(token == null || token == '')) {
      if (this.authService.isTokenValid()) {
        return this.checkModulePermission(state.url);
      }
      this._cS.logOut();
      return false;
    }

    if (window.location.href.includes('?t=')) {
      sessionStorage.setItem('redirectURL', window.location.href);
    }
    this._router.navigate(['/login']);
    return false;
  }

  /** Block direct URL access when the role lacks module view permission. */
  private checkModulePermission(url: string): boolean | UrlTree {
    const path = url.split('?')[0];
    if (path.startsWith('/brooklyn/my-profile')) {
      return true;
    }

    const routes = this.getProtectedRoutes();
    const matched = routes.find((route) => path.startsWith(route.url));
    if (!matched || matched.permission) {
      return true;
    }

    const landingUrl = this.getLandingUrl(routes);
    return this._router.parseUrl(landingUrl);
  }

  private getLandingUrl(routes: RoutePermission[]): string {
    const settingsOrder = [
      '/brooklyn/users',
      '/brooklyn/roles',
      '/brooklyn/agency',
      '/brooklyn/accounts',
      '/brooklyn/types',
      '/brooklyn/sites',
      '/brooklyn/suppliers',
      '/brooklyn/reps',
      '/brooklyn/vendors',
      '/brooklyn/resources',
      '/brooklyn/programs',
      '/brooklyn/badges',
      '/brooklyn/under-review',
      '/brooklyn/terms',
      '/brooklyn/maintenance',
      '/brooklyn/announcements',
      '/brooklyn/general',
    ];
    const mainOrder = [
      '/brooklyn/overview',
      '/brooklyn/dashboard',
      '/brooklyn/investments',
      '/brooklyn/expenses',
      '/brooklyn/transfers',
      '/brooklyn/projections',
      '/brooklyn/reports',
      '/brooklyn/cashflow',
    ];
    const byUrl = new Map(routes.map((route) => [route.url, route.permission]));
    for (const url of settingsOrder.concat(mainOrder)) {
      if (byUrl.get(url)) {
        return url;
      }
    }
    return '/page-not-found';
  }

  private getProtectedRoutes(): RoutePermission[] {
    const routes: RoutePermission[] = [
      { permission: !!this._iS.canAccessUsers().canViewUsers, url: '/brooklyn/users' },
      { permission: !!this._iS.canAccessRoles().canViewRole, url: '/brooklyn/roles' },
      { permission: !!this._iS.canAccessAgencys().canViewAgency, url: '/brooklyn/agency' },
      { permission: !!this._iS.canAccessAccounts().canViewAccounts, url: '/brooklyn/accounts' },
      {
        permission: !!this._iS.canAccessAccountTypes().canViewAccounts,
        url: '/brooklyn/types',
      },
      { permission: !!this._iS.canAccessSites().canViewSites, url: '/brooklyn/sites' },
      {
        permission: !!this._iS.canAccessSuppliers().canViewSuppliers,
        url: '/brooklyn/suppliers',
      },
      { permission: !!this._iS.canAccessReps().canViewReps, url: '/brooklyn/reps' },
      { permission: !!this._iS.canAccessVendors().canViewVendors, url: '/brooklyn/vendors' },
      {
        permission: !!this._iS.canAccessResources().canViewResources,
        url: '/brooklyn/resources',
      },
      {
        permission: !!this._iS.canAccessRetailPrograms().canViewRetailPrograms,
        url: '/brooklyn/programs',
      },
      { permission: !!this._iS.canAccessBadges().canViewBadge, url: '/brooklyn/badges' },
      {
        permission: !!this._iS.canAccessUnderReview().canViewUnderReview,
        url: '/brooklyn/under-review',
      },
      {
        permission: !!this._iS.canAccessTerms().canViewTerms,
        url: '/brooklyn/terms',
      },
      {
        permission: !!this._iS.canAccessMaintainance().canViewUsers,
        url: '/brooklyn/maintenance',
      },
      {
        permission: !!this._iS.canAccessAnnouncement().canViewAnnouncements,
        url: '/brooklyn/announcements',
      },
      { permission: !!this._iS.canAccessGeneral().canViewGeneral, url: '/brooklyn/general' },
      { permission: !!this._iS.canviewOverview(), url: '/brooklyn/overview' },
      { permission: !!this._iS.canViewDashboard(), url: '/brooklyn/dashboard' },
      {
        permission: !!this._iS.canAccessInvestments().canViewInvestment,
        url: '/brooklyn/investments',
      },
      { permission: !!this._iS.canAccessBills().canViewExpenses, url: '/brooklyn/expenses' },
      {
        permission: !!this._iS.canAccessTransfers().canViewTransfers,
        url: '/brooklyn/transfers',
      },
      { permission: !!this._iS.canViewProjections(), url: '/brooklyn/projections' },
      { permission: !!this._iS.canViewReports(), url: '/brooklyn/reports' },
      { permission: !!this._iS.canviewCashflow(), url: '/brooklyn/cashflow' },
    ];

    return routes.sort((a, b) => b.url.length - a.url.length);
  }
}
